import { useCallback } from 'react';
import { DataTable } from '../ui/DataTable';
import { useAccountingStore } from '../../stores/accountingStore';
import { useCurrency } from '../../hooks/useCurrency';
import { Download } from 'lucide-react';
import { exportToExcel } from '../../utils/export/exportUtils';
import type { TrialBalanceEntry } from '../../types/filters';

/**
 * Props pour la vue de balance - supporte le nouveau format de l'API
 */
interface BalanceViewProps {
  accounts: TrialBalanceEntry[];
  loading?: boolean;
}

/**
 * Extrait le solde brut d'une entrée de balance
 * Compatible avec l'ancien format (balance: number) et le nouveau (balance: object)
 */
const getBalanceValue = (entry: TrialBalanceEntry): number => {
  if (typeof entry.balance === 'number') {
    return entry.balance;
  }
  return entry.balance?.raw ?? (entry.debit - entry.credit);
};

/**
 * Obtient le solde débiteur d'une entrée
 */
const getDebitBalance = (entry: TrialBalanceEntry): number => {
  if (typeof entry.balance === 'object' && 'debitBalance' in entry.balance) {
    return entry.balance.debitBalance;
  }
  const raw = getBalanceValue(entry);
  return raw > 0 ? raw : 0;
};

/**
 * Obtient le solde créditeur d'une entrée
 */
const getCreditBalance = (entry: TrialBalanceEntry): number => {
  if (typeof entry.balance === 'object' && 'creditBalance' in entry.balance) {
    return entry.balance.creditBalance;
  }
  const raw = getBalanceValue(entry);
  return raw < 0 ? Math.abs(raw) : 0;
};

// Type pour la ligne de table avec id obligatoire
type BalanceTableRow = TrialBalanceEntry & { id: string };

export function BalanceView({ accounts, loading }: BalanceViewProps) {
  const { mode } = useAccountingStore();
  const { currentCurrency, baseCurrency, formatConverted } = useCurrency();

  // Protection pour les données undefined/null
  const safeAccounts = Array.isArray(accounts) ? accounts : [];

  // Utilise le formatage avec conversion de devise
  const formatAmount = (amount: number) => {
    return formatConverted(amount);
  };

  // Convertir les données pour avoir un id
  const tableData: BalanceTableRow[] = safeAccounts.map(item => ({
    ...item,
    id: item.account.id
  }));

  const columns = [
    {
      header: 'Code',
      accessor: (item: BalanceTableRow) => (
        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{item.account.code}</span>
      ),
      width: 100,
      sortable: true
    },
    {
      header: 'Libellé',
      accessor: (item: BalanceTableRow) => item.account.name,
      minWidth: 200,
      sortable: true
    },
    {
      header: 'Type',
      accessor: (item: BalanceTableRow) => {
        const typeLabels: Record<string, string> = {
          asset: 'Actif',
          liability: 'Passif',
          equity: 'Capitaux propres',
          revenue: 'Produits',
          expense: 'Charges'
        };
        const typeColors: Record<string, string> = {
          asset: 'text-blue-600 bg-blue-50',
          liability: 'text-red-600 bg-red-50',
          equity: 'text-purple-600 bg-purple-50',
          revenue: 'text-green-600 bg-green-50',
          expense: 'text-orange-600 bg-orange-50'
        };
        return (
          <span className={`text-xs px-2 py-1 rounded-full ${typeColors[item.account.type] || ''}`}>
            {typeLabels[item.account.type] || item.account.type}
          </span>
        );
      },
      width: 130,
      sortable: true
    },
    {
      header: 'Débit',
      accessor: (item: BalanceTableRow) => formatAmount(item.debit),
      className: 'text-right font-mono',
      width: 130,
      sortable: true
    },
    {
      header: 'Crédit',
      accessor: (item: BalanceTableRow) => formatAmount(item.credit),
      className: 'text-right font-mono',
      width: 130,
      sortable: true
    },
    {
      header: 'Solde Débiteur',
      accessor: (item: BalanceTableRow) => {
        const debitBal = getDebitBalance(item);
        return debitBal > 0 ? (
          <span className="text-green-600 dark:text-green-400">{formatAmount(debitBal)}</span>
        ) : '-';
      },
      className: 'text-right font-mono',
      width: 130,
      sortable: true
    },
    {
      header: 'Solde Créditeur',
      accessor: (item: BalanceTableRow) => {
        const creditBal = getCreditBalance(item);
        return creditBal > 0 ? (
          <span className="text-red-600 dark:text-red-400">{formatAmount(creditBal)}</span>
        ) : '-';
      },
      className: 'text-right font-mono',
      width: 130,
      sortable: true
    }
  ];

  const totals = safeAccounts.reduce((acc, item) => ({
    debit: acc.debit + item.debit,
    credit: acc.credit + item.credit,
    debitBalance: acc.debitBalance + getDebitBalance(item),
    creditBalance: acc.creditBalance + getCreditBalance(item)
  }), { debit: 0, credit: 0, debitBalance: 0, creditBalance: 0 });

  // Vérification de l'équilibre
  const isBalanced = Math.abs(totals.debit - totals.credit) < 0.01;
  const difference = totals.debit - totals.credit;

  const handleExportXLSX = useCallback(() => {
    const rows = tableData.map(row => ({
      Code: row.account?.code || '',
      'Libellé': row.account?.name || '',
      Type: row.account?.type || '',
      'Débit': row.debit,
      'Crédit': row.credit,
      'Solde Débiteur': getDebitBalance(row),
      'Solde Créditeur': getCreditBalance(row),
    }));
    const filename = `balance_verification_${new Date().toISOString().slice(0, 10)}`;
    exportToExcel(rows, ['Code', 'Libellé', 'Type', 'Débit', 'Crédit', 'Solde Débiteur', 'Solde Créditeur'], filename);
  }, [tableData]);

  return (
    <div className="space-y-4">
      <div className="bg-primary/5 p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h3 className="font-medium">Balance de Vérification {mode}</h3>
            <p className="text-sm text-text-secondary">
              {mode === 'SYSCOHADA' 
                ? 'Balance selon le Système Comptable OHADA Révisé'
                : 'Balance selon les normes IFRS'
              }
              {currentCurrency !== baseCurrency && (
                <span className="ml-2 text-xs font-medium text-primary">
                  (Montants affichés en {currentCurrency})
                </span>
              )}
            </p>
          </div>
          {tableData.length > 0 && (
            <button
              onClick={handleExportXLSX}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              title="Exporter la balance en XLSX"
            >
              <Download className="h-3.5 w-3.5" />
              XLSX
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={tableData}
        loading={loading}
        emptyMessage="Aucun compte trouvé"
        emptyTitle="Aucune donnée"
        showToolbar={true}
        showPagination={true}
        pageSize={50}
        enableResize={true}
        enableSort={true}
        enableFilters={true}
        enableColumnToggle={true}
        enableGlobalFilter={true}
        storageKey="balance-view"
        maxHeight="calc(100vh - 450px)"
        density="compact"
      />

      <div className="bg-tertiary p-4 rounded-lg">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-text-secondary">Total Débit</p>
            <p className="text-lg font-medium text-text-primary">{formatAmount(totals.debit)}</p>
          </div>
          <div>
            <p className="text-sm text-text-secondary">Total Crédit</p>
            <p className="text-lg font-medium text-text-primary">{formatAmount(totals.credit)}</p>
          </div>
          <div>
            <p className="text-sm text-text-secondary">Total Soldes Débiteurs</p>
            <p className="text-lg font-medium text-text-primary">{formatAmount(totals.debitBalance)}</p>
          </div>
          <div>
            <p className="text-sm text-text-secondary">Total Soldes Créditeurs</p>
            <p className="text-lg font-medium text-text-primary">{formatAmount(totals.creditBalance)}</p>
          </div>
        </div>
        
        {/* Indicateur d'équilibre */}
        <div className={`mt-4 p-3 rounded-lg ${isBalanced ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${isBalanced ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className={`font-medium ${isBalanced ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
              {isBalanced 
                ? 'Balance équilibrée ✓' 
                : `Différence: ${formatAmount(Math.abs(difference))} ${difference > 0 ? '(Débit > Crédit)' : '(Crédit > Débit)'}`
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}